// Canvas rendering + viewport math.
//
// Owns the DOM references for the workspace canvas, its CSS transform
// wrapper, the rect-selection overlay, and the brush-radius cursor.
// Exposes rendering primitives (redraw, applyTransform, drawRectOverlay)
// and viewport helpers (fitToScreen, actualSize, screenToImage,
// updateCursor).
//
// Side effects that aren't strictly rendering (refreshing tool buttons
// when a rect is cleared, drawing the component overlay on top of the
// transformed canvas) are injected via setRenderHooks().

import { state, MaterialLayer } from '../state';
import { $ } from '../utils/dom';
import { clamp } from '../utils/clamp';

export const canvas = $<HTMLCanvasElement>('canvas');
export const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
export const canvasWrap = $('canvas-wrap');
export const workspace = $('workspace');
export const cursor = $('cursor-overlay');
export const rectOverlay = $<HTMLCanvasElement>('rect-overlay');
const rectOverlayCtx = rectOverlay.getContext('2d')!;
export const materialOverlay = $<HTMLCanvasElement>('material-overlay');
const materialOverlayCtx = materialOverlay.getContext('2d')!;

// One offscreen canvas per material. Created lazily, repainted from
// material.data only when needed. Cleared when a material is removed.
const materialCanvasCache = new Map<string, HTMLCanvasElement>();

function getMaterialCanvas(m: MaterialLayer): HTMLCanvasElement {
  let c = materialCanvasCache.get(m.id);
  if (!c || c.width !== m.w || c.height !== m.h) {
    c = document.createElement('canvas');
    c.width = m.w;
    c.height = m.h;
    materialCanvasCache.set(m.id, c);
  }
  c.getContext('2d')!.putImageData(
    new ImageData(m.data as Uint8ClampedArray<ArrayBuffer>, m.w, m.h),
    0, 0,
  );
  return c;
}

export function invalidateMaterialCache(id?: string): void {
  if (id) materialCanvasCache.delete(id);
  else materialCanvasCache.clear();
}

interface RenderHooks {
  refreshToolButtons?: () => void;
  drawComponentOverlay?: () => void;
}
let hooks: RenderHooks = {};

export function setRenderHooks(h: RenderHooks): void {
  hooks = h;
}

export function redraw(): void {
  if (!state.workData) return;
  ctx.putImageData(
    new ImageData(state.workData as Uint8ClampedArray<ArrayBuffer>, state.imgW, state.imgH),
    0, 0,
  );
  // Composite each material on top of the base. drawImage honours the
  // material's alpha so transparent pixels show the base through.
  // While a resize is in flight, scale the *active* material to the
  // preview bbox via drawImage's stretch form — the underlying pixel
  // buffer isn't rebuilt until pointerup, so quality is preserved.
  for (const m of state.materials) {
    const previewing = state.resizePreview
      && state.activeMaterialId === m.id;
    if (previewing) {
      const p = state.resizePreview!;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(getMaterialCanvas(m), p.x, p.y, p.w, p.h);
    } else {
      ctx.drawImage(getMaterialCanvas(m), m.x, m.y);
    }
  }
  // applyTransform repaints the material outline (and any other overlay
  // that lives inside #canvas-wrap), so there's no separate call here.
  applyTransform();
}

export function applyTransform(): void {
  canvasWrap.style.transform =
    `translate(${state.panX}px, ${state.panY}px) scale(${state.zoom})`;
  canvasWrap.style.width = state.imgW + 'px';
  canvasWrap.style.height = state.imgH + 'px';
  if (state.separateMode || state.cleanupMode) hooks.drawComponentOverlay?.();
  if (state.rectSelection) drawRectOverlay();
  drawMaterialOverlay();
}

// Dashed outline around the currently-selected material so the user
// knows which layer their brush / wand will edit and what they're
// about to drag with the move tool. While the move tool is active we
// also draw 8 small handles (4 corners + 4 edge midpoints) so the user
// can grab them to resize.
//
// The overlay canvas is dynamically sized + repositioned each call so
// it always covers the material bbox AND the handle outset — without
// it, a material scaled larger than the base canvas would have its
// outline clipped at the base edges.
export function drawMaterialOverlay(): void {
  if (!state.imgW) {
    materialOverlay.classList.remove('show');
    return;
  }
  const active = state.activeMaterialId
    ? state.materials.find(m => m.id === state.activeMaterialId)
    : null;
  if (!active) {
    materialOverlay.classList.remove('show');
    return;
  }

  // The outline traces the preview bbox while a resize is in flight,
  // so the user sees the target dimensions even before the rebuild.
  const p = state.resizePreview;
  const bx = p ? p.x : active.x;
  const by = p ? p.y : active.y;
  const bw = p ? p.w : active.w;
  const bh = p ? p.h : active.h;

  // Size the overlay canvas to fully cover the bbox plus a margin for
  // the handles. Positioned within #canvas-wrap (which carries the
  // zoom/pan transform), so we can extend it beyond the base canvas
  // edges using negative top/left in image-space coordinates.
  const hSize = Math.max(4, 9 / state.zoom);
  const margin = Math.ceil(hSize + 2);
  const minX = Math.floor(bx - margin);
  const minY = Math.floor(by - margin);
  const maxX = Math.ceil(bx + bw + margin);
  const maxY = Math.ceil(by + bh + margin);
  const ovW = Math.max(1, maxX - minX);
  const ovH = Math.max(1, maxY - minY);
  if (materialOverlay.width !== ovW)  materialOverlay.width  = ovW;
  if (materialOverlay.height !== ovH) materialOverlay.height = ovH;
  materialOverlay.style.left = minX + 'px';
  materialOverlay.style.top  = minY + 'px';
  materialOverlay.classList.add('show');

  // Translate everything from base-canvas coords into overlay-local
  // coords by subtracting the overlay's image-space origin.
  const ox = -minX, oy = -minY;

  const c = materialOverlayCtx;
  c.clearRect(0, 0, ovW, ovH);
  const lineW = Math.max(0.5, 2 / state.zoom);
  const dash  = Math.max(2, 8 / state.zoom);
  const gap   = Math.max(1, 4 / state.zoom);
  c.lineWidth = lineW;
  c.strokeStyle = '#ffce4d';
  c.setLineDash([dash, gap]);
  c.strokeRect(
    bx + ox + lineW / 2,
    by + oy + lineW / 2,
    Math.max(0, bw - lineW),
    Math.max(0, bh - lineW),
  );
  c.setLineDash([]);

  // Handles — drawn only when the move tool is active so the dashed
  // outline doesn't look interactive in tools that ignore handles.
  if (state.tool !== 'move') return;
  const half = hSize / 2;
  const hx = [bx + ox, bx + ox + bw / 2, bx + ox + bw];
  const hy = [by + oy, by + oy + bh / 2, by + oy + bh];
  c.lineWidth = Math.max(0.5, 1.5 / state.zoom);
  c.strokeStyle = '#000';
  c.fillStyle = '#ffce4d';
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      // Skip the centre — only edges and corners are draggable.
      if (i === 1 && j === 1) continue;
      const cx = hx[i] - half;
      const cy = hy[j] - half;
      c.fillRect(cx, cy, hSize, hSize);
      c.strokeRect(cx, cy, hSize, hSize);
    }
  }
}

export function drawRectOverlay(): void {
  if (!state.rectSelection || !state.imgW) {
    rectOverlay.classList.remove('show');
    return;
  }
  rectOverlay.width = state.imgW;
  rectOverlay.height = state.imgH;
  rectOverlay.classList.add('show');

  const c = rectOverlayCtx;
  c.clearRect(0, 0, state.imgW, state.imgH);

  const { minX, minY, maxX, maxY } = state.rectSelection;
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;

  // Highlight the active region (inside or outside the rect).
  if (state.rectInverse) {
    c.fillStyle = 'rgba(220, 70, 70, 0.12)';
    c.fillRect(0, 0, state.imgW, state.imgH);
    c.clearRect(minX, minY, w, h);
  } else {
    c.fillStyle = 'rgba(124, 209, 124, 0.14)';
    c.fillRect(minX, minY, w, h);
  }

  const lineW = Math.max(0.5, 2 / state.zoom);
  const dash = Math.max(2, 8 / state.zoom);
  const gap  = Math.max(1, 4 / state.zoom);
  c.lineWidth = lineW;
  c.strokeStyle = state.rectInverse ? '#dc4646' : '#3aa55a';
  c.setLineDash([dash, gap]);
  c.strokeRect(minX + lineW / 2, minY + lineW / 2,
               Math.max(0, w - lineW), Math.max(0, h - lineW));
  c.setLineDash([]);
}

export function clearRectSelection(): void {
  state.rectSelection = null;
  state.rectDragStart = null;
  drawRectOverlay();
  hooks.refreshToolButtons?.();
}

export function fitToScreen(updateStatus?: () => void): void {
  if (!state.imgW) return;
  const r = workspace.getBoundingClientRect();
  const margin = 40;
  state.zoom = Math.min(
    (r.width - margin) / state.imgW,
    (r.height - margin) / state.imgH,
  );
  state.zoom = clamp(state.zoom, 0.05, 16);
  state.panX = (r.width - state.imgW * state.zoom) / 2;
  state.panY = (r.height - state.imgH * state.zoom) / 2;
  applyTransform();
  updateStatus?.();
}

export function actualSize(updateStatus?: () => void): void {
  if (!state.imgW) return;
  const r = workspace.getBoundingClientRect();
  state.zoom = 1;
  state.panX = (r.width - state.imgW) / 2;
  state.panY = (r.height - state.imgH) / 2;
  applyTransform();
  updateStatus?.();
}

export function screenToImage(screenX: number, screenY: number): { x: number; y: number } {
  const r = workspace.getBoundingClientRect();
  const x = (screenX - r.left - state.panX) / state.zoom;
  const y = (screenY - r.top  - state.panY) / state.zoom;
  return { x, y };
}

export function updateCursor(screenX: number, screenY: number): void {
  if (!state.workData) {
    cursor.style.display = 'none';
    return;
  }
  if (state.isPanning || state.spaceHeld || state.separateMode || state.cleanupMode) {
    cursor.style.display = 'none';
    return;
  }
  // The brush-radius circle only makes sense for the painting tools. Wand
  // and rect-select operate per-click / per-drag so the circle is noise.
  if (state.tool !== 'erase' && state.tool !== 'restore') {
    cursor.style.display = 'none';
    return;
  }
  // pointermove is bound on window, so the cursor can sit on the toolbar
  // / status bar / outside the page — don't draw the brush ring there.
  const r = workspace.getBoundingClientRect();
  if (screenX < r.left || screenX > r.right || screenY < r.top || screenY > r.bottom) {
    cursor.style.display = 'none';
    return;
  }
  cursor.style.display = 'block';
  cursor.style.left = (screenX - r.left) + 'px';
  cursor.style.top  = (screenY - r.top) + 'px';
  const screenSize = state.brushSize * state.zoom * 2;
  cursor.style.width = screenSize + 'px';
  cursor.style.height = screenSize + 'px';
  // Reaching here means tool is 'erase' or 'restore' (others returned above).
  cursor.style.borderColor = state.tool === 'restore' ? '#7cd17c' : '#fff';
  cursor.classList.toggle('is-restore', state.tool === 'restore');
}
